const Sentry = require('@sentry/browser');

/**
 * Sentry へ送信しないエラーメッセージ.
 *
 * ここに並べるのは「xPink のコードでは発生し得ない」ノイズだけに限定する.
 * ブラウザ拡張機能や GTM 経由で注入されたサードパーティスクリプトが投げるもので,
 * 実際のユーザ体験には影響がなく, こちらで修正する手段も無い.
 *
 * SDK 標準の除外リスト (Script error. / Cannot redefine property: googletag 等) は
 * 自動的にマージされるため, ここでは重複して書かない.
 *
 * @see https://docs.sentry.io/platforms/javascript/configuration/filtering/
 */
const IGNORE_ERRORS = [
    // ウォレット / AI アシスタント等の拡張機能が window のプロパティを再定義しようとして落ちる.
    // 標準リストの googletag と同種で, ページ側の JS は正常に動作している.
    // (XPINK-EC-4Q: Cannot redefine property: modelContext)
    /Cannot redefine property: (modelContext|ethereum|solana|tronLink|keplr)/,
    // MetaMask 等のウォレット拡張. サイト側からは呼び出していない.
    /Invalid method: ethereum/,
    /Failed to connect to MetaMask/,
    // 拡張機能の content script が, 閉じられたタブへメッセージを送ろうとして失敗する.
    /Invalid call to runtime\.sendMessage\(\)/,
    /Extension context invalidated/,
    // 広告ブロッカー / トラッキング防止で計測タグの読み込みが握り潰されたケース.
    // 計測が飛ばないだけで購入導線には影響しない.
    /^twq is not defined$/,
    /^fbq is not defined$/,
    /^gtag is not defined$/,
];

/**
 * 発生元 URL で送信対象から除外するパターン.
 *
 * ブラウザ拡張機能や, アプリ内ブラウザが注入するスクリプト (webkit-masked-url) は
 * xPink のデプロイ対象外であり, スタックトレースも難読化されていて追跡できない.
 */
const DENY_URLS = [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
    /^chrome:\/\//i,
    /^webkit-masked-url:/i,
];

const EXTENSION_FRAME_PATTERN = /^(chrome|moz|safari-web|safari)-extension:\/\/|^chrome:\/\/|^webkit-masked-url:/i;

/**
 * スタックフレームが全て拡張機能由来のイベントを落とす.
 *
 * DENY_URLS は最下層フレームの URL しか見ないため, 拡張機能のスクリプトが
 * 複数フレームを積んだ場合に取りこぼす. こちらは xPink のファイルが
 * 1 フレームも含まれないことを確認してから捨てるので, 自前のコードが
 * 絡んだエラーは残る.
 *
 * @param {object} event Sentry イベント
 *
 * @return {object|null} 送信する場合はイベント, 除外する場合は null
 */
function dropBrowserExtensionEvents(event) {
    const filenames = [];

    ((event.exception && event.exception.values) || []).forEach((value) => {
        ((value.stacktrace && value.stacktrace.frames) || []).forEach((frame) => {
            if (typeof frame.filename === 'string' && frame.filename !== '') {
                filenames.push(frame.filename);
            }
        });
    });

    if (filenames.length > 0 && filenames.every((filename) => EXTENSION_FRAME_PATTERN.test(filename))) {
        return null;
    }

    return event;
}

/**
 * URL のクエリ文字列やフォームデータのうち, 値を伏せるキーの判定パターン.
 *
 * PHP 側の Customize\Sentry\SensitiveDataScrubber と同じ方針で,
 * パスワード・トークン・ログイン ID といった機密情報を送信対象から外す.
 */
const SENSITIVE_KEY_PATTERN = /(?:pass|pwd|secret|token|credential|authorization|auth[-_]?key|api[-_]?key|access[-_]?key|private[-_]?key|signature|session|cookie|csrf|login[-_]?(?:id|email)|username|user[-_]?id|e?mail|card[-_]?(?:no|num|number)|cvv|cvc|security[-_]?code|expire|birth|tel|phone|zip)/i;

const FILTERED = '[Filtered]';

/**
 * URL のクエリ文字列から機密パラメータの値を伏せる.
 *
 * ログイン・会員登録の導線は GET パラメータを使わないが, 外部からの流入や
 * リダイレクトで意図せずクエリに値が乗るケースがあるため, 送信前に落としておく.
 *
 * @param {string} url 対象の URL (絶対・相対いずれも可)
 *
 * @return {string} 機密パラメータを伏せた URL
 */
function scrubUrl(url) {
    if (typeof url !== 'string' || url.indexOf('?') === -1) {
        return url;
    }

    try {
        const parsed = new URL(url, window.location.origin);
        let modified = false;

        parsed.searchParams.forEach((value, key) => {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                modified = true;
            }
        });

        if (!modified) {
            return url;
        }

        Array.from(parsed.searchParams.keys()).forEach((key) => {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                parsed.searchParams.set(key, FILTERED);
            }
        });

        // 元が相対 URL の場合はオリジンを付け足さずに返す.
        return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? parsed.toString() : parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
        // URL として解釈できない場合はクエリごと落とす (機密情報を残さない側に倒す).
        return url.split('?')[0];
    }
}

/**
 * イベントに載る URL から機密情報を除去する.
 *
 * @param {object} event Sentry イベント
 *
 * @return {object} URL を伏せ字にしたイベント
 */
function scrubSensitiveData(event) {
    if (event.request && event.request.url) {
        event.request.url = scrubUrl(event.request.url);
    }

    if (event.request && event.request.query_string) {
        delete event.request.query_string;
    }

    // ブラウザ SDK は既定でリクエストボディを送らないが, 将来 SDK 側の既定が
    // 変わっても漏れないよう明示的に落としておく.
    if (event.request && event.request.data) {
        delete event.request.data;
    }

    (event.breadcrumbs || []).forEach((breadcrumb) => {
        if (breadcrumb.data && typeof breadcrumb.data.url === 'string') {
            breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
        }

        if (breadcrumb.data && typeof breadcrumb.data.to === 'string') {
            breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
        }

        if (breadcrumb.data && typeof breadcrumb.data.from === 'string') {
            breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
        }
    });

    return event;
}

if (typeof window !== 'undefined' && window.__SENTRY_DSN__) {
    Sentry.init({
        dsn: window.__SENTRY_DSN__,
        environment: window.__SENTRY_ENVIRONMENT__ || 'dev',
        release: window.__SENTRY_RELEASE__ || undefined,
        ignoreErrors: IGNORE_ERRORS,
        denyUrls: DENY_URLS,
        // ブラウザから送る URL / パンくずに機密情報が載らないようにする.
        sendDefaultPii: false,
        beforeSend: (event) => {
            const kept = dropBrowserExtensionEvents(event);

            return kept === null ? null : scrubSensitiveData(kept);
        },
    });
}

module.exports = Sentry;
